import React from 'react';
import {
  ALL_SITE_FILTER,
  normalizeSiteFilterChange,
  readSessionFilterState,
  resolveSiteFilterLocationIds,
  sanitizeSiteFilterValues,
  writeSessionFilterState,
  type SiteFilterRegion,
} from '@/domain/library';
import { useSavedViews, type SavedViewsController, type SavedViewsPrefs } from '../../../hooks/useSavedViews';
import type { WarehouseTabKey } from './warehouseTabs';

export type WarehouseGrouping = 'off' | 'date' | 'status' | 'vendor' | 'site';
export type WarehouseViewMode = 'list' | 'grid' | 'calendar';
export type WarehouseSortDir = 'asc' | 'desc';

/**
 * Everything a Warehouse list view remembers. A saved view stores all of it ("the search, sites,
 * type, status, grouping, fold, sort and List or Grid"); the session keeps it per tab and profile.
 */
export type WarehouseListConfig = {
  search: string;
  /** Site filter values (site ids, region:… values or the all-sites value). */
  sites: string[];
  /** Transfers: direction both|out|in. POs: all|goods|labor. Subrentals: all|equipment|vehicles. */
  facet: string;
  statuses: string[];
  /** Quick filters, e.g. past_ship_date, arriving_today, linked_to_event. */
  chips: string[];
  grouping: WarehouseGrouping;
  /** Fold the stale R2 backlog into one row (on by default). */
  fold: boolean;
  sortKey: string;
  sortDir: WarehouseSortDir;
  view: WarehouseViewMode;
  /** Ship (transfers), expected (POs) or rental window (subrentals) range. */
  dateFrom: string;
  dateTo: string;
  /** Created (transfers) or ordered (POs) range. */
  secondDateFrom: string;
  secondDateTo: string;
  vendorIds: string[];
  orderedBy: string;
};

/** Fields the Advanced panel owns; its Reset puts back only these. */
const ADVANCED_KEYS = [
  'dateFrom',
  'dateTo',
  'secondDateFrom',
  'secondDateTo',
  'vendorIds',
  'orderedBy',
  'grouping',
  'fold',
  'sortKey',
  'sortDir',
] as const satisfies ReadonlyArray<keyof WarehouseListConfig>;

const GROUPINGS: readonly WarehouseGrouping[] = ['off', 'date', 'status', 'vendor', 'site'];
const VIEW_MODES: readonly WarehouseViewMode[] = ['list', 'grid', 'calendar'];

const sessionKey = (tab: WarehouseTabKey) => `warehouse.${tab}.filters.session`;
// Each tab's default view loads once per browser session, and never over filters the URL or the
// session set, over an event link, or over a change the viewer made while preferences were loading.
const defaultViewAppliedTabs = new Set<WarehouseTabKey>();

function sortedCopy(values: readonly string[]): string[] {
  return [...values].map(String).sort();
}

/**
 * The config in one fixed key order with sorted lists, so two equal configs always serialize the
 * same way (stored views come back from jsonb with their keys reordered).
 */
function canonicalConfig(config: WarehouseListConfig): WarehouseListConfig {
  return {
    search: config.search.trim(),
    sites: sortedCopy(config.sites),
    facet: config.facet,
    statuses: sortedCopy(config.statuses),
    chips: sortedCopy(config.chips),
    grouping: config.grouping,
    fold: config.fold,
    sortKey: config.sortKey,
    sortDir: config.sortDir,
    view: config.view,
    dateFrom: config.dateFrom,
    dateTo: config.dateTo,
    secondDateFrom: config.secondDateFrom,
    secondDateTo: config.secondDateTo,
    vendorIds: sortedCopy(config.vendorIds),
    orderedBy: config.orderedBy,
  };
}

export function areWarehouseConfigsEqual(
  left: WarehouseListConfig | null | undefined,
  right: WarehouseListConfig | null | undefined,
): boolean {
  if (!left || !right) return left === right;
  return JSON.stringify(canonicalConfig(left)) === JSON.stringify(canonicalConfig(right));
}

/** A stored or applied config with every field checked; anything missing or malformed takes the default. */
function mergeStoredConfig(defaults: WarehouseListConfig, stored: unknown): WarehouseListConfig {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return defaults;
  const value = stored as { [key: string]: unknown };
  const text = (key: keyof WarehouseListConfig): string => {
    const raw = value[key];
    return typeof raw === 'string' ? raw : defaults[key] as string;
  };
  const list = (key: keyof WarehouseListConfig): string[] => {
    const raw = value[key];
    return Array.isArray(raw) ? raw.map(String) : defaults[key] as string[];
  };
  const grouping = GROUPINGS.find((candidate) => candidate === value.grouping) ?? defaults.grouping;
  const view = VIEW_MODES.find((candidate) => candidate === value.view) ?? defaults.view;
  return canonicalConfig({
    search: text('search'),
    sites: list('sites'),
    facet: text('facet'),
    statuses: list('statuses'),
    chips: list('chips'),
    grouping,
    fold: typeof value.fold === 'boolean' ? value.fold : defaults.fold,
    sortKey: text('sortKey'),
    sortDir: value.sortDir === 'desc' ? 'desc' : value.sortDir === 'asc' ? 'asc' : defaults.sortDir,
    view,
    dateFrom: text('dateFrom'),
    dateTo: text('dateTo'),
    secondDateFrom: text('secondDateFrom'),
    secondDateTo: text('secondDateTo'),
    vendorIds: list('vendorIds'),
    orderedBy: text('orderedBy'),
  });
}

type UseWarehouseListStateOptions = {
  tab: WarehouseTabKey;
  profileId: string | null;
  prefs: SavedViewsPrefs;
  prefsLoaded: boolean;
  defaults: WarehouseListConfig;
  /**
   * Filters from the URL (legacy tab values). Read once, when the tab mounts; they win over the
   * session and the default view.
   */
  overrides?: Partial<WarehouseListConfig> | null;
  /** Views the tab offers; a stored view mode the tab lacks falls back to List. */
  views: readonly WarehouseViewMode[];
  /** Every status the picker offers: picking all of them sends no status filter. */
  allStatuses: readonly string[];
  /** Site filter option ids once they have loaded; stale stored values are dropped then. */
  siteOptionIds: ReadonlySet<string> | null;
  regions: SiteFilterRegion[];
  /**
   * Inputs outside the config that change the result set (the linked event). While set, the list
   * starts from the defaults: on mount, and in the same render whenever it changes.
   */
  scopeKey?: string | null;
  /** Sort keys that start descending (newest or largest first). */
  descendingFirst?: readonly string[];
};

export type WarehouseSortControl = { direction: WarehouseSortDir | null; onSort: () => void };

export type WarehouseListState = {
  config: WarehouseListConfig;
  defaults: WarehouseListConfig;
  update: (patch: Partial<WarehouseListConfig>) => void;
  /** Swaps in a whole config (a saved view); the search applies at once. */
  replace: (next: WarehouseListConfig) => void;
  /** Puts back the Advanced panel's fields only (the Advanced Reset). */
  resetAdvanced: () => void;
  /** Puts back every filter except the view mode (the phone sheet's Reset). */
  resetAll: () => void;
  debouncedSearch: string;
  page: number;
  setPage: (page: number) => void;
  /** The folded backlog is shown in the list. */
  showFolded: boolean;
  toggleFolded: () => void;
  savedViews: SavedViewsController<WarehouseListConfig>;
  /** How many Advanced fields differ from the defaults. */
  advancedActiveCount: number;
  /** Status filter for the server: the whole list when every status is picked; null when none is. */
  statusesParam: string[] | null;
  /** No status is picked, so nothing can match and no request is sent. */
  noStatuses: boolean;
  statusFilterActive: boolean;
  selectedStatusSet: Set<string>;
  locationIds: string[] | null;
  siteFilterActive: boolean;
  toggleChip: (key: string) => void;
  sortFor: (key: string) => WarehouseSortControl;
  setSites: (values: string[]) => void;
  setStatuses: (statuses: string[]) => void;
  setVendorIds: (vendorIds: string[]) => void;
  setGrouping: (value: string) => void;
};

function sameMembers(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) return false;
  const set = new Set(left);
  return right.every((value) => set.has(value));
}

export function useWarehouseListState({
  tab,
  profileId,
  prefs,
  prefsLoaded,
  defaults,
  overrides,
  views,
  allStatuses,
  siteOptionIds,
  regions,
  scopeKey,
  descendingFirst,
}: UseWarehouseListStateOptions): WarehouseListState {
  const withAllowedView = React.useCallback(
    (next: WarehouseListConfig): WarehouseListConfig => (views.includes(next.view) ? next : { ...next, view: 'list' }),
    [views],
  );
  const [initial] = React.useState(() => {
    // An event's link (scopeKey) lists every record of that event, so it starts from the defaults plus
    // the URL's presets: filters restored from the session, or a default view, could hide some of them.
    const stored = scopeKey ? null : readSessionFilterState<unknown>(sessionKey(tab), profileId);
    const base = mergeStoredConfig(defaults, stored);
    const hasOverrides = Boolean(overrides && Object.keys(overrides).length > 0);
    const merged = hasOverrides ? canonicalConfig({ ...base, ...overrides }) : base;
    return { config: withAllowedView(merged), hadSessionState: stored != null, hadOverrides: hasOverrides };
  });
  const [config, setConfig] = React.useState<WarehouseListConfig>(initial.config);
  const [debouncedSearch, setDebouncedSearch] = React.useState(initial.config.search.trim());
  const [showFolded, setShowFolded] = React.useState(false);
  const touchedRef = React.useRef(false);

  // Following an event link while the tab is open also starts from the defaults (keeping the view
  // mode), in the same render, so no request goes out with the old filters and the new event.
  const [appliedScope, setAppliedScope] = React.useState<string | null>(scopeKey ?? null);
  if ((scopeKey ?? null) !== appliedScope) {
    setAppliedScope(scopeKey ?? null);
    if (scopeKey) {
      setConfig(withAllowedView({ ...defaults, view: config.view }));
      setDebouncedSearch(defaults.search.trim());
      setShowFolded(false);
    }
  }

  React.useEffect(() => {
    const next = config.search.trim();
    if (next === debouncedSearch) return undefined;
    const handle = window.setTimeout(() => setDebouncedSearch(next), 250);
    return () => window.clearTimeout(handle);
  }, [config.search, debouncedSearch]);

  React.useEffect(() => {
    writeSessionFilterState(sessionKey(tab), profileId, config);
  }, [config, profileId, tab]);

  // Sites stored in an older session (a region the viewer lost, a closed site) drop once the
  // options load, instead of silently widening the list to every site.
  React.useEffect(() => {
    if (!siteOptionIds || siteOptionIds.size === 0) return;
    setConfig((current) => {
      const kept = sanitizeSiteFilterValues(current.sites, new Set(siteOptionIds), true) ?? [ALL_SITE_FILTER];
      return sameMembers(kept, current.sites) ? current : { ...current, sites: kept };
    });
  }, [siteOptionIds]);

  // Any change to what the list contains starts again at page 1, in the same render (no request
  // goes out with the new filters at the old offset).
  const signature = React.useMemo(
    () => JSON.stringify([canonicalConfig({ ...config, search: debouncedSearch, view: 'list' }), showFolded, scopeKey ?? null]),
    [config, debouncedSearch, scopeKey, showFolded],
  );
  const [pageState, setPageState] = React.useState(() => ({ signature, page: 1 }));
  const page = pageState.signature === signature ? pageState.page : 1;
  const setPage = React.useCallback((next: number) => {
    setPageState({ signature, page: Math.max(1, Math.floor(next)) });
  }, [signature]);

  const update = React.useCallback((patch: Partial<WarehouseListConfig>) => {
    touchedRef.current = true;
    setConfig((current) => ({ ...current, ...patch }));
  }, []);
  const replace = React.useCallback((next: WarehouseListConfig) => {
    touchedRef.current = true;
    const merged = withAllowedView(mergeStoredConfig(defaults, next));
    setConfig(merged);
    setDebouncedSearch(merged.search.trim());
  }, [defaults, withAllowedView]);
  const resetAdvanced = React.useCallback(() => {
    touchedRef.current = true;
    setConfig((current) => {
      const next = { ...current };
      ADVANCED_KEYS.forEach((key) => {
        Object.assign(next, { [key]: defaults[key] });
      });
      return next;
    });
  }, [defaults]);
  const resetAll = React.useCallback(() => {
    touchedRef.current = true;
    setConfig((current) => ({ ...defaults, view: current.view }));
    setDebouncedSearch(defaults.search.trim());
  }, [defaults]);
  const toggleFolded = React.useCallback(() => setShowFolded((current) => !current), []);

  const savedViews = useSavedViews<WarehouseListConfig>({
    prefs,
    viewsKey: `warehouse.${tab}.savedViews`,
    defaultKey: `warehouse.${tab}.defaultViewId`,
    current: config,
    apply: replace,
    equals: areWarehouseConfigsEqual,
  });
  const { views: savedViewList, defaultViewId, setAppliedId } = savedViews;

  React.useEffect(() => {
    if (!prefsLoaded || defaultViewAppliedTabs.has(tab)) return;
    defaultViewAppliedTabs.add(tab);
    // Never over an event link: the view's filters could hide the event's records.
    if (initial.hadOverrides || initial.hadSessionState || touchedRef.current || appliedScope) return;
    const view = defaultViewId ? savedViewList.find((candidate) => candidate.id === defaultViewId) : null;
    if (!view?.config) return;
    replace(view.config);
    setAppliedId(view.id);
  }, [appliedScope, defaultViewId, initial, prefsLoaded, replace, savedViewList, setAppliedId, tab]);

  const advancedActiveCount = React.useMemo(() => {
    const current = canonicalConfig(config);
    const base = canonicalConfig(defaults);
    return ADVANCED_KEYS.filter((key) => JSON.stringify(current[key]) !== JSON.stringify(base[key])).length;
  }, [config, defaults]);

  const noStatuses = config.statuses.length === 0;
  // The page functions read a missing list as "open statuses only" (coalesce(p_statuses, v_open)),
  // so every status picked sends the whole list, in its canonical order. None picked sends nothing:
  // the tab shows "Pick at least one status" instead.
  const statusesParam = React.useMemo(() => {
    if (noStatuses) return null;
    return allStatuses.every((status) => config.statuses.includes(status)) ? [...allStatuses] : config.statuses;
  }, [allStatuses, config.statuses, noStatuses]);
  const selectedStatusSet = React.useMemo(() => new Set(config.statuses), [config.statuses]);
  const locationIds = React.useMemo(
    () => resolveSiteFilterLocationIds(config.sites, regions) ?? null,
    [config.sites, regions],
  );

  const toggleChip = React.useCallback((key: string) => {
    touchedRef.current = true;
    setConfig((current) => ({
      ...current,
      chips: current.chips.includes(key) ? current.chips.filter((chip) => chip !== key) : [...current.chips, key],
    }));
  }, []);
  const sortFor = React.useCallback((key: string): WarehouseSortControl => ({
    direction: config.sortKey === key ? config.sortDir : null,
    onSort: () => {
      if (config.sortKey === key) update({ sortDir: config.sortDir === 'asc' ? 'desc' : 'asc' });
      else update({ sortKey: key, sortDir: descendingFirst?.includes(key) ? 'desc' : 'asc' });
    },
  }), [config.sortDir, config.sortKey, descendingFirst, update]);
  const setSites = React.useCallback((values: string[]) => {
    touchedRef.current = true;
    setConfig((current) => ({ ...current, sites: normalizeSiteFilterChange(values, current.sites) }));
  }, []);
  const setStatuses = React.useCallback((statuses: string[]) => update({ statuses }), [update]);
  const setVendorIds = React.useCallback((vendorIds: string[]) => update({ vendorIds }), [update]);
  const setGrouping = React.useCallback((value: string) => {
    const grouping = GROUPINGS.find((candidate) => candidate === value);
    if (grouping) update({ grouping });
  }, [update]);

  return {
    config,
    defaults,
    update,
    replace,
    resetAdvanced,
    resetAll,
    debouncedSearch,
    page,
    setPage,
    showFolded,
    toggleFolded,
    savedViews,
    advancedActiveCount,
    statusesParam,
    noStatuses,
    statusFilterActive: !sameMembers(config.statuses, defaults.statuses),
    selectedStatusSet,
    locationIds,
    siteFilterActive: !config.sites.includes(ALL_SITE_FILTER),
    toggleChip,
    sortFor,
    setSites,
    setStatuses,
    setVendorIds,
    setGrouping,
  };
}
